import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import dashboardServices from "app/services/dashboard-services";
import {downloadBuffer} from "app/utils/appHelpers";
import {DASHBOARD_COLORS, metricChipSx} from "./chartColors";
import ExcelExportButton from "app/widgets/Shared/ExcelExportButton";

const QUICK_RANGES = [
    {label: "7D", days: 7},
    {label: "30D", days: 30},
    {label: "90D", days: 90}
];

const SELECT_ALL_VALUE = "__all__";
const formatQty = (value) => Number(value || 0).toLocaleString("es-CL");

const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const buildQuickRange = (days) => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - (days - 1));
    return {
        desde: toDateInputValue(start),
        hasta: toDateInputValue(now)
    };
};

const DriverBreakdownTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
            }}
        >
            <Typography variant="subtitle2" sx={{mb: 0.75}}>{label}</Typography>
            {payload.map((item) => (
                <Typography key={item.dataKey} variant="body2" sx={{color: item.color}}>
                    {item.name}: {formatQty(item.value)}
                </Typography>
            ))}
            <Typography variant="body2" sx={{mt: 0.75, pt: 0.75, borderTop: "1px solid", borderColor: "divider", fontWeight: 600}}>Total: {formatQty(payload.reduce((sum, item) => sum + Number(item.value || 0), 0))}</Typography>
        </Box>
    );
};

const DriverOrdersBreakdown = () => {
    const [quickRange, setQuickRange] = useState(30);
    const [filters, setFilters] = useState(() => buildQuickRange(30));
    const [drivers, setDrivers] = useState([]);
    const [selectedDriverIds, setSelectedDriverIds] = useState(null);
    const [showTotal, setShowTotal] = useState(false);
    const [series, setSeries] = useState([]);
    const [totals, setTotals] = useState({
        pending_programmed: 0,
        in_progress: 0,
        finished: 0,
        total_orders: 0
    });
    const [loading, setLoading] = useState(true);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [error, setError] = useState("");
    const [chartFilter, setChartFilter] = useState(null);

    const allDriverIds = useMemo(() => drivers.map((driver) => driver.id), [drivers]);
    const hasInvalidRange = Boolean(filters.desde && filters.hasta && filters.desde > filters.hasta);

    const allSelected = useMemo(() => {
        if (drivers.length === 0 || !Array.isArray(selectedDriverIds)) return false;
        return selectedDriverIds.length === drivers.length;
    }, [drivers.length, selectedDriverIds]);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            if (!filters.desde || !filters.hasta || filters.desde > filters.hasta) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            try {
                const requestFilters = {...filters};
                if (Array.isArray(selectedDriverIds)) requestFilters.driver_ids = selectedDriverIds;
                const response = await dashboardServices.getDriverOrderBreakdown(requestFilters);

                if (!isMounted) return;

                const responseDrivers = response?.drivers ?? [];
                setDrivers(responseDrivers);

                if (selectedDriverIds === null && responseDrivers.length > 0) {
                    setSelectedDriverIds(responseDrivers.map((driver) => driver.id));
                }

                setSeries(response?.series ?? []);
                setTotals(response?.totals ?? {
                    pending_programmed: 0,
                    in_progress: 0,
                    finished: 0,
                    total_orders: 0
                });
            } catch (loadError) {
                if (!isMounted) return;
                setSeries([]);
                setTotals({
                    pending_programmed: 0,
                    in_progress: 0,
                    finished: 0,
                    total_orders: 0
                });
                setError("No fue posible cargar el desglose por conductor.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();
        return () => {
            isMounted = false;
        };
    }, [filters, selectedDriverIds]);

    const chartData = useMemo(() => {
        if (showTotal) {
            const allItems = [
                {key: 'pending_programmed', label: "Pendientes", count: totals.pending_programmed, color: DASHBOARD_COLORS.pendingProgrammed},
                {key: 'in_progress', label: "En progreso", count: totals.in_progress, color: DASHBOARD_COLORS.inProgress},
                {key: 'finished', label: "Finalizados", count: totals.finished, color: DASHBOARD_COLORS.finished}
            ];
            return chartFilter ? allItems.filter((item) => item.key === chartFilter) : allItems;
        }

        return [...series]
            .sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0))
            .map((row) => ({
                ...row,
                label: row.driver_name
            }));
    }, [showTotal, series, totals, chartFilter]);

    const handleQuickRange = (days) => {
        setQuickRange(days);
        setFilters(buildQuickRange(days));
    };

    const handleDriversChange = (event) => {
        const {value} = event.target;
        const asArray = typeof value === "string" ? value.split(",") : value;

        if (asArray.includes(SELECT_ALL_VALUE)) {
            if (allSelected) {
                setSelectedDriverIds([]);
            } else {
                setSelectedDriverIds(allDriverIds);
            }
            return;
        }

        const parsedIds = asArray.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
        setSelectedDriverIds(parsedIds);
    };

    const handleDownloadExcel = async () => {
        if (hasInvalidRange || downloadLoading) return;

        setDownloadLoading(true);
        try {
            const requestFilters = {...filters};
            if (Array.isArray(selectedDriverIds)) requestFilters.driver_ids = selectedDriverIds;
            const data = await dashboardServices.downloadDriverOrderBreakdownXlsx(requestFilters);
            const signature = new Uint8Array(data).slice(0, 2);
            const isZipContainer = signature.length === 2 && signature[0] === 0x50 && signature[1] === 0x4B;
            if (!isZipContainer) throw new Error("invalid_xlsx_payload");
            const stamp = new Date().toISOString().split("T")[0];
            downloadBuffer(data, `desglose_conductores_${stamp}.xlsx`);
        } catch (downloadError) {
            setError("No fue posible descargar el Excel del desglose por conductor.");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <Card sx={{borderRadius: 3, border: "1px solid", borderColor: "divider"}}>
            <CardContent sx={{p: {xs: 2, md: 3}}}>
                <Stack spacing={2.5}>
                    <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" spacing={1.5}>
                        <Stack spacing={0.5}>
                            <Typography variant="h4">Desglose por conductor</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Eje Y: cantidad de pedidos | Eje X: conductores
                            </Typography>
                        </Stack>

                        <ExcelExportButton
                            onClick={handleDownloadExcel}
                            loading={downloadLoading}
                            disabled={loading || hasInvalidRange}
                            sx={{alignSelf: "flex-start"}}
                        />
                    </Stack>

                    <Stack direction={{xs: "column", lg: "row"}} spacing={1.5}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {QUICK_RANGES.map((option) => (
                                <Chip
                                    key={option.days}
                                    label={option.label}
                                    clickable
                                    color={quickRange === option.days ? "primary" : "default"}
                                    variant={quickRange === option.days ? "filled" : "outlined"}
                                    onClick={() => handleQuickRange(option.days)}
                                />
                            ))}
                        </Stack>

                        <TextField
                            size="small"
                            label="Desde"
                            type="date"
                            value={filters.desde}
                            onChange={(event) => setFilters((prev) => ({...prev, desde: event.target.value}))}
                            InputLabelProps={{shrink: true}}
                        />

                        <TextField
                            size="small"
                            label="Hasta"
                            type="date"
                            value={filters.hasta}
                            onChange={(event) => setFilters((prev) => ({...prev, hasta: event.target.value}))}
                            InputLabelProps={{shrink: true}}
                        />

                        <FormControl size="small" sx={{minWidth: 320}}>
                            <InputLabel>Conductores</InputLabel>
                            <Select
                                multiple
                                value={selectedDriverIds ?? []}
                                onChange={handleDriversChange}
                                input={<OutlinedInput label="Conductores"/>}
                                renderValue={(selected) => {
                                    if (drivers.length === 0) return "Sin conductores";
                                    if (selected.length === 0) return "Sin conductores seleccionados";
                                    if (selected.length === drivers.length) return "Todos los conductores";
                                    const namesById = drivers.reduce((acc, driver) => {
                                        acc[driver.id] = driver.name;
                                        return acc;
                                    }, {});
                                    return selected.map((id) => namesById[id]).filter(Boolean).join(", ");
                                }}
                            >
                                <MenuItem value={SELECT_ALL_VALUE}>
                                    <Checkbox checked={allSelected}/>
                                    <ListItemText primary="Seleccionar todos"/>
                                </MenuItem>
                                {drivers.map((driver) => (
                                    <MenuItem key={driver.id} value={driver.id}>
                                        <Checkbox checked={(selectedDriverIds ?? []).includes(driver.id)}/>
                                        <ListItemText primary={driver.name}/>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={<Checkbox checked={showTotal} onChange={(event) => setShowTotal(event.target.checked)}/>}
                            label="Mostrar total"
                        />
                    </Stack>

                    {hasInvalidRange && (
                        <Alert severity="warning">La fecha inicial no puede ser mayor que la fecha final.</Alert>
                    )}

                    {error && (
                        <Alert severity="error">{error}</Alert>
                    )}

                    {showTotal && (
                        <Alert severity="info">
                            Vista total activada: se muestran 3 categorías agregadas.
                        </Alert>
                    )}

                    {!showTotal && Array.isArray(selectedDriverIds) && selectedDriverIds.length === 0 && (
                        <Alert severity="info">
                            No hay conductores seleccionados para el desglose.
                        </Alert>
                    )}

                    <Grid container spacing={1}>
                        <Grid item>
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.pendingProgrammed, chartFilter === 'pending_programmed')}
                                label={`Pendientes: ${formatQty(totals.pending_programmed)}`}
                                onClick={() => setChartFilter(chartFilter === 'pending_programmed' ? null : 'pending_programmed')}
                                clickable
                            />
                        </Grid>
                        <Grid item>
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.inProgress, chartFilter === 'in_progress')}
                                label={`En progreso: ${formatQty(totals.in_progress)}`}
                                onClick={() => setChartFilter(chartFilter === 'in_progress' ? null : 'in_progress')}
                                clickable
                            />
                        </Grid>
                        <Grid item>
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.finished, chartFilter === 'finished')}
                                label={`Finalizados: ${formatQty(totals.finished)}`}
                                onClick={() => setChartFilter(chartFilter === 'finished' ? null : 'finished')}
                                clickable
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{height: 420, overflowX: showTotal ? "hidden" : "auto"}}>
                        {loading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{height: "100%"}}>
                                <CircularProgress/>
                            </Stack>
                        ) : (
                            <Box sx={{width: "100%", minWidth: showTotal ? "100%" : Math.max(900, chartData.length * 110)}}>
                                <ResponsiveContainer width="100%" height={390}>
                                    <BarChart data={chartData} margin={{top: 12, right: 18, left: 0, bottom: 24}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB"/>
                                        <XAxis
                                            dataKey="label"
                                            interval={0}
                                            angle={showTotal ? 0 : -20}
                                            textAnchor={showTotal ? "middle" : "end"}
                                            height={showTotal ? 40 : 72}
                                        />
                                        <YAxis allowDecimals={false}/>
                                        <Tooltip content={<DriverBreakdownTooltip/>}/>
                                        {!showTotal && <Legend/>}

                                        {showTotal ? (
                                            <Bar dataKey="count" name="Total pedidos" radius={[8, 8, 0, 0]}>
                                                {chartData.map((entry) => (
                                                    <Cell key={entry.label} fill={entry.color}/>
                                                ))}
                                            </Bar>
                                        ) : (
                                            <React.Fragment>
                                                {(chartFilter === null || chartFilter === 'pending_programmed') && (
                                                    <Bar dataKey="pending_programmed" name="Pendientes" fill={DASHBOARD_COLORS.pendingProgrammed} radius={[8, 8, 0, 0]}/>
                                                )}
                                                {(chartFilter === null || chartFilter === 'in_progress') && (
                                                    <Bar dataKey="in_progress" name="En progreso" fill={DASHBOARD_COLORS.inProgress} radius={[8, 8, 0, 0]}/>
                                                )}
                                                {(chartFilter === null || chartFilter === 'finished') && (
                                                    <Bar dataKey="finished" name="Finalizados" fill={DASHBOARD_COLORS.finished} radius={[8, 8, 0, 0]}/>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default DriverOrdersBreakdown;
